// src/stores/assetStore.ts
import { proxy } from 'valtio';
import type { Asset, AuditRecord } from '../types/asset';
import { api } from '../utils/api';

export type { Asset, AuditRecord };

interface AssetState {
  assets: Asset[];
  loading: boolean;
  searchTerm: string;

  iso27001Filter: string;
}

export const assetStore = proxy<AssetState>({
  assets: [],
  loading: true,
  searchTerm: '',

  iso27001Filter: 'all',
});

export const assetActions = {
  loadAssets: async () => {
    try {
      assetStore.loading = true;
      const data = await api.get('/assets') as Asset[];
      assetStore.assets = data;
    } catch (e) {
      console.error('Failed to load assets from API:', e);
      // Fallback to localStorage for offline/migration
      const stored = localStorage.getItem('assets');
      if (stored) {
        try { assetStore.assets = JSON.parse(stored); } catch {}
      }
    } finally {
      assetStore.loading = false;
    }
  },

  saveAssets: async () => {
    // No-op: individual CRUD operations handle persistence now
  },

  addAsset: async (asset: Asset) => {
    assetStore.assets.push(asset);
    try {
      const serverAsset = await api.post('/assets', asset) as Asset;
      // Replace optimistic entry with server response (has real ID, ref number, timestamps)
      const idx = assetStore.assets.findIndex(a => a.id === asset.id);
      if (idx !== -1) {
        assetStore.assets[idx] = serverAsset;
      }
    } catch (e) {
      // Roll back optimistic update on failure
      const idx = assetStore.assets.findIndex(a => a.id === asset.id);
      if (idx !== -1) assetStore.assets.splice(idx, 1);
      console.error('Failed to save asset to API:', e);
      throw e;
    }
  },

  updateAsset: async (id: string, updates: Partial<Asset>) => {
    const index = assetStore.assets.findIndex(a => a.id === id);
    if (index !== -1) {
      const previous = { ...assetStore.assets[index] };
      // Send the store's current updatedAt so the server can use it for optimistic locking.
      // The server will set the new timestamp — do NOT override updatedAt before sending.
      const payload = { ...assetStore.assets[index], ...updates, updatedAt: previous.updatedAt };
      // Optimistic local update with a provisional timestamp
      assetStore.assets[index] = { ...payload, updatedAt: new Date().toISOString() };
      try {
        const serverResponse = await api.put(`/assets/${id}`, payload) as Partial<Asset>;
        // Update with server-confirmed updatedAt
        if (serverResponse.updatedAt) {
          assetStore.assets[index] = { ...assetStore.assets[index], updatedAt: serverResponse.updatedAt };
        }
      } catch (e: unknown) {
        // Roll back optimistic update on failure
        assetStore.assets[index] = previous;
        const err = e as Error & { status?: number };
        if (err.status === 409) {
          // Conflict — reload latest data from server
          console.warn('Conflict detected on asset update, reloading...');
          await assetActions.loadAssets();
        }
        console.error('Failed to update asset on API:', e);
        throw e;
      }
    }
  },

  deleteAsset: async (id: string) => {
    const index = assetStore.assets.findIndex(a => a.id === id);
    if (index !== -1) {
      const removed = assetStore.assets[index];
      assetStore.assets.splice(index, 1);
      try {
        await api.del(`/assets/${id}`);
      } catch (e) {
        // Roll back optimistic update on failure
        assetStore.assets.splice(index, 0, removed);
        console.error('Failed to delete asset from API:', e);
        throw e;
      }
    }
  },

  getAssetById: (id: string): Asset | undefined => {
    return assetStore.assets.find(a => a.id === id);
  },

  // Filter actions
  setSearchTerm: (term: string) => {
    assetStore.searchTerm = term;
  },


  setISO27001Filter: (filter: string) => {
    assetStore.iso27001Filter = filter;
  },

  // Get unique asset groups
  getUniqueAssetGroups: (): string[] => {
    return [...new Set(assetStore.assets.map(a => a.assetGroup).filter(Boolean))].sort();
  },

  // Generate reference number for new assets
  generateReferenceNumber: (): string => {
    const year = new Date().getFullYear();
    const existingNumbers = assetStore.assets
      .map(a => a.referenceNumber)
      .filter(ref => ref && ref.startsWith(`A-${year}-`))
      .map(ref => parseInt(ref.split('-')[2]))
      .filter(num => !isNaN(num));

    const nextNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1;
    return `A-${year}-${String(nextNumber).padStart(4, '0')}`;
  },
};

export function getUniqueAssetGroups(): string[] {
  return [...new Set(assetStore.assets.map(a => a.assetGroup).filter(Boolean))].sort();
}

/** Returns asset groups organised by ISO 27001 category, with the assets in each group */
export function getAssetGroupsByCategory(): Map<string, Map<string, Asset[]>> {
  const result = new Map<string, Map<string, Asset[]>>();
  assetStore.assets.forEach(asset => {
    if (!asset.iso27001Category || !asset.assetGroup) return;
    if (!result.has(asset.iso27001Category)) {
      result.set(asset.iso27001Category, new Map());
    }
    const categoryMap = result.get(asset.iso27001Category)!;
    if (!categoryMap.has(asset.assetGroup)) {
      categoryMap.set(asset.assetGroup, []);
    }
    categoryMap.get(asset.assetGroup)!.push(asset);
  });
  return result;
}

/** Returns a map of asset group name → total count of assets in that group */
export function getAssetGroupCounts(): Map<string, number> {
  const result = new Map<string, number>();
  assetStore.assets.forEach(asset => {
    if (asset.assetGroup) {
      result.set(asset.assetGroup, (result.get(asset.assetGroup) || 0) + 1);
    }
  });
  return result;
}

/** Returns the count of assets matching a specific group + category combo */
export function getGroupCategoryCount(assetGroup: string, category?: string): number {
  return assetStore.assets.filter(a => {
    if (!a.assetGroup || a.assetGroup !== assetGroup) return false;
    if (category && a.iso27001Category !== category) return false;
    return true;
  }).length;
}
